<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';
import OnboardingSplitLayout from './shared/OnboardingSplitLayout.vue';
import OnboardingPage from './shared/OnboardingPage.vue';
import OnboardingSection from './shared/OnboardingSection.vue';
import OptionRow from './shared/OptionRow.vue';
import ChatCornerScene from './stage/ChatCornerScene.vue';
import InboxChannelsDialog from './inbox-setup/InboxChannelsDialog.vue';
import ChannelToggleRow from './inbox-setup/ChannelToggleRow.vue';
import WebWidgetCreationStatus from './inbox-setup/WebWidgetCreationStatus.vue';
import HelpCenterCreationStatus from './inbox-setup/HelpCenterCreationStatus.vue';
import { CHANNEL_TYPES } from 'dashboard/helper/inbox';
import { useChannelConnect } from './inbox-setup/useChannelConnect';
import { useChannelConfig } from './inbox-setup/useChannelConfig';
import { findConnectedInbox } from './inbox-setup/channelMatchers';
import { DIALOG_CHANNELS } from './inbox-setup/constants';
import { ONBOARDING_STAGE_NOTIFICATIONS } from './shared/constants';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId, currentAccount, finishOnboarding } = useAccount();
const { isEnterprise } = useConfig();
const { connectViaOAuth, connectWhatsapp } = useChannelConnect();
const { isConfigured } = useChannelConfig();

const helpCenterGenerationId = computed(
  () => currentAccount.value?.custom_attributes?.help_center_generation_id
);

const isSubmitting = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

// This route is the backend's `inbox_setup` step, presented as three internal
// pages: channels, first responder, and widget branding. Only the final page
// completes the step.
const PAGE_CHANNELS = 'channels';
const PAGE_HANDLER = 'handler';
const PAGE_BRAND = 'brand';
const page = ref(PAGE_CHANNELS);
const currentStep = computed(
  () => ({ [PAGE_CHANNELS]: 3, [PAGE_HANDLER]: 4, [PAGE_BRAND]: 5 })[page.value]
);

// The channels offered as toggle rows, in display order. `key` matches
// ONBOARDING_STAGE_NOTIFICATIONS and the CHANNELS_STEP i18n entries; `inbox`
// is an inbox-shaped stub for the icon and connected-inbox matching.
const TOGGLE_CHANNELS = [
  {
    key: 'website',
    type: CHANNEL_TYPES.WEBSITE,
    inbox: { channel_type: 'Channel::WebWidget' },
  },
  {
    key: 'email',
    type: CHANNEL_TYPES.EMAIL,
    inbox: { channel_type: 'Channel::Email' },
  },
  {
    key: 'whatsapp',
    type: CHANNEL_TYPES.WHATSAPP,
    inbox: { channel_type: 'Channel::Whatsapp' },
  },
  {
    key: 'instagram',
    type: CHANNEL_TYPES.INSTAGRAM,
    inbox: { channel_type: 'Channel::Instagram' },
  },
  {
    key: 'messenger',
    type: CHANNEL_TYPES.FACEBOOK,
    inbox: { channel_type: 'Channel::FacebookPage' },
  },
  {
    key: 'telegram',
    type: CHANNEL_TYPES.TELEGRAM,
    inbox: { channel_type: 'Channel::Telegram' },
  },
];

// Channels with no connect flow in this phase: the web widget is auto-created
// during account details, and email is set up later in-app — toggling them only
// marks the selection.
const NO_CONNECT_TYPES = [CHANNEL_TYPES.WEBSITE, CHANNEL_TYPES.EMAIL];

// Hide channels whose installation OAuth credentials are missing — their
// connect flow would only error.
const channelRows = computed(() =>
  TOGGLE_CHANNELS.filter(channel => isConfigured(channel.type)).map(
    channel => ({
      ...channel,
      name: t(
        `ONBOARDING_V2.CHANNELS_STEP.CHANNELS.${channel.key.toUpperCase()}.NAME`
      ),
      description: t(
        `ONBOARDING_V2.CHANNELS_STEP.CHANNELS.${channel.key.toUpperCase()}.DESCRIPTION`
      ),
      connectedInbox: findConnectedInbox(inboxes.value, channel.inbox),
    })
  )
);

// Toggled channels, in toggle order, driving the stage notification stack.
const selectedKeys = ref([]);
const isSelected = key => selectedKeys.value.includes(key);

// Channels already backed by a real inbox — including the auto-created web
// widget once its poll lands — surface as toggled-on without user action.
watch(
  () =>
    channelRows.value
      .filter(channel => channel.connectedInbox)
      .map(channel => channel.key),
  connectedKeys => {
    connectedKeys.forEach(key => {
      if (!isSelected(key)) selectedKeys.value.push(key);
    });
  },
  { immediate: true }
);

const channelsDialogRef = ref(null);

// The initial inboxes fetch happens in WebWidgetCreationStatus, which polls
// `inboxes/get` from its own mount — no need to dispatch it here too.
onMounted(() => {
  useTrack(ONBOARDING_EVENTS.INBOX_SETUP_VISITED);
});

const openChannelsDialog = type => channelsDialogRef.value?.open(type);
const refetchInboxes = () => store.dispatch('inboxes/get');

// WhatsApp connects via Meta's embedded-signup popup; the DIALOG_CHANNELS open
// the channels dialog preselected to their in-dialog step; the rest go through
// the redirect OAuth flow (Instagram by channel type).
const connectChannel = channel => {
  if (channel.type === CHANNEL_TYPES.WHATSAPP) {
    connectWhatsapp();
    return;
  }
  if (DIALOG_CHANNELS.includes(channel.type)) {
    openChannelsDialog(channel.type);
    return;
  }
  connectViaOAuth(channel.inbox?.provider || channel.type);
};

const toggleChannel = channel => {
  if (channel.connectedInbox) return;
  const index = selectedKeys.value.indexOf(channel.key);
  if (index >= 0) {
    selectedKeys.value.splice(index, 1);
    return;
  }
  selectedKeys.value.push(channel.key);
  if (!NO_CONNECT_TYPES.includes(channel.type)) connectChannel(channel);
};

// Closing the connect dialog without finishing leaves nothing connected — undo
// the optimistic toggle so the row reflects reality. If the connect did land,
// the connected-inboxes watcher re-selects (and locks) the row on refetch.
const revertUnconnectedDialogChannels = () => {
  selectedKeys.value = selectedKeys.value.filter(key => {
    const channel = channelRows.value.find(c => c.key === key);
    if (!channel || !DIALOG_CHANNELS.includes(channel.type)) return true;
    return Boolean(channel.connectedInbox);
  });
};

const stageNotifications = computed(() =>
  selectedKeys.value.map(key => {
    const { id, nameKey, messageKey, colorClass } =
      ONBOARDING_STAGE_NOTIFICATIONS[key];
    return { id, name: t(nameKey), message: t(messageKey), colorClass };
  })
);

// Who answers first. Persisted as the conversation_handler custom attribute
// with the step-completion call — the onboarding endpoint requires a valid
// onboarding_step, so there is no standalone PATCH for it.
const handler = ref('');
const HANDLER_VALUES = { ai: 'ai_agent', team: 'team' };
const handlerOptions = computed(() =>
  ['ai', 'team'].map(value => ({
    value,
    label: t(`ONBOARDING_V2.HANDLER_STEP.OPTIONS.${value.toUpperCase()}.LABEL`),
    description: t(
      `ONBOARDING_V2.HANDLER_STEP.OPTIONS.${value.toUpperCase()}.DESCRIPTION`
    ),
  }))
);

// Widget branding. Theme and accent drive the live stage preview; the accent
// is written to the auto-created web widget inbox on finish.
const DEFAULT_ACCENT_COLOR = '#6e56cf';
const theme = ref('bright');
const accentColor = ref(DEFAULT_ACCENT_COLOR);
const themeOptions = computed(() =>
  ['bright', 'dark'].map(value => ({
    value,
    label: t(`ONBOARDING_V2.BRAND_STEP.THEME.${value.toUpperCase()}`),
  }))
);
const swatches = computed(() =>
  [
    ['#6e56cf', 'VIOLET'],
    ['#2f6fed', 'BLUE'],
    ['#3fca7d', 'GREEN'],
    ['#e5484d', 'RED'],
    ['#17151f', 'BLACK'],
  ].map(([value, labelKey]) => ({
    value,
    label: t(`ONBOARDING_V2.BRAND_STEP.COLORS.${labelKey}`),
  }))
);

const completeOnboarding = async (event, { applyWidgetColor = false } = {}) => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    if (applyWidgetColor) {
      const widgetInbox = findConnectedInbox(inboxes.value, {
        channel_type: 'Channel::WebWidget',
      });
      if (widgetInbox && widgetInbox.widget_color !== accentColor.value) {
        await store.dispatch('inboxes/updateInbox', {
          id: widgetInbox.id,
          formData: false,
          channel: { widget_color: accentColor.value },
        });
      }
    }
    // Declare the step we're completing so the controller only clears it when
    // the stored step still matches (idempotent). setUser then refreshes the
    // auth store so the router guard sees the cleared step and lets us in.
    await finishOnboarding({
      onboarding_step: 'inbox_setup',
      ...(handler.value
        ? { conversation_handler: HANDLER_VALUES[handler.value] }
        : {}),
    });
    useTrack(event);
    await store.dispatch('setUser');
    router.push({ name: 'home', params: { accountId: accountId.value } });
  } catch {
    useAlert(t('ONBOARDING_INBOX_SETUP.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleFinish = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_COMPLETED, {
    applyWidgetColor: true,
  });
const handleSkip = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_SKIPPED);
</script>

<template>
  <div>
    <OnboardingSplitLayout :total-steps="5" :current-step="currentStep">
      <template #brand>
        <Logo class="h-6 w-auto" />
      </template>

      <!-- Page 3: channels -->
      <OnboardingPage
        v-if="page === PAGE_CHANNELS"
        :greeting="t('ONBOARDING_V2.CHANNELS_STEP.TITLE')"
        :subtitle="t('ONBOARDING_V2.CHANNELS_STEP.SUBTITLE')"
        :continue-label="t('ONBOARDING_INBOX_SETUP.CONTINUE')"
        @continue="page = PAGE_HANDLER"
      >
        <template #greeting-icon>
          <Icon icon="i-lucide-wrench" class="size-4 text-n-slate-7" />
        </template>

        <OnboardingSection
          :title="t('ONBOARDING_V2.CHANNELS_STEP.SETUP_SECTION_TITLE')"
          icon="i-lucide-sparkles"
        >
          <div class="divide-y divide-n-weak">
            <WebWidgetCreationStatus />
            <HelpCenterCreationStatus
              v-if="isEnterprise && helpCenterGenerationId"
            />
          </div>
        </OnboardingSection>

        <OnboardingSection
          :title="t('ONBOARDING_V2.CHANNELS_STEP.SECTION_TITLE')"
          icon="i-lucide-inbox"
        >
          <ChannelToggleRow
            v-for="channel in channelRows"
            :key="channel.key"
            :name="channel.name"
            :description="channel.description"
            :inbox="channel.inbox"
            :on="isSelected(channel.key)"
            :locked="Boolean(channel.connectedInbox)"
            @toggle="toggleChannel(channel)"
          />
        </OnboardingSection>
      </OnboardingPage>

      <!-- Page 4: who answers first -->
      <OnboardingPage
        v-else-if="page === PAGE_HANDLER"
        :greeting="t('ONBOARDING_V2.HANDLER_STEP.TITLE')"
        :subtitle="t('ONBOARDING_V2.HANDLER_STEP.SUBTITLE')"
        :continue-label="t('ONBOARDING_INBOX_SETUP.CONTINUE')"
        :skip-label="t('ONBOARDING_INBOX_SETUP.SKIP')"
        :disabled="!handler"
        @continue="page = PAGE_BRAND"
        @skip="page = PAGE_BRAND"
      >
        <OnboardingSection
          :title="t('ONBOARDING_V2.HANDLER_STEP.SECTION_TITLE')"
          icon="i-lucide-messages-square"
        >
          <div role="radiogroup">
            <OptionRow
              v-for="option in handlerOptions"
              :key="option.value"
              :label="option.label"
              :description="option.description"
              :selected="handler === option.value"
              @select="handler = option.value"
            />
          </div>
        </OnboardingSection>
      </OnboardingPage>

      <!-- Page 5: widget branding -->
      <OnboardingPage
        v-else
        :greeting="t('ONBOARDING_V2.BRAND_STEP.TITLE')"
        :subtitle="t('ONBOARDING_V2.BRAND_STEP.SUBTITLE')"
        :continue-label="t('ONBOARDING_V2.BRAND_STEP.FINISH')"
        :skip-label="t('ONBOARDING_INBOX_SETUP.SKIP')"
        :is-loading="isSubmitting"
        @continue="handleFinish"
        @skip="handleSkip"
      >
        <OnboardingSection
          :title="t('ONBOARDING_V2.BRAND_STEP.SECTION_TITLE')"
          icon="i-lucide-palette"
        >
          <div class="grid grid-cols-2 items-center px-3 py-3">
            <div class="flex items-center gap-2">
              <Icon
                icon="i-lucide-sun-moon"
                class="size-4 flex-shrink-0 text-n-slate-9"
              />
              <span class="text-n-slate-11">
                {{ t('ONBOARDING_V2.BRAND_STEP.THEME_LABEL') }}
              </span>
            </div>
            <div class="flex justify-end">
              <div
                role="radiogroup"
                class="grid w-[12.5rem] grid-cols-2 rounded-[0.625rem] border border-n-weak p-[3px]"
              >
                <button
                  v-for="option in themeOptions"
                  :key="option.value"
                  type="button"
                  role="radio"
                  :aria-checked="theme === option.value"
                  class="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
                  :class="
                    theme === option.value
                      ? 'bg-n-slate-12 text-n-slate-1'
                      : 'text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="theme = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>
          <div
            class="grid grid-cols-2 items-center border-t border-n-weak px-3 py-3"
          >
            <div class="flex items-center gap-2">
              <Icon
                icon="i-lucide-palette"
                class="size-4 flex-shrink-0 text-n-slate-9"
              />
              <span class="text-n-slate-11">
                {{ t('ONBOARDING_V2.BRAND_STEP.COLOR_LABEL') }}
              </span>
            </div>
            <div class="flex justify-end gap-2">
              <!-- Swatch colors are user-picked widget accents, so the style
                   binding is required (same as ChatCornerScene). -->
              <button
                v-for="swatch in swatches"
                :key="swatch.value"
                type="button"
                role="radio"
                :aria-checked="accentColor === swatch.value"
                :aria-label="swatch.label"
                class="size-[1.625rem] rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] transition-transform hover:scale-110"
                :class="
                  accentColor === swatch.value
                    ? 'ring-2 ring-n-slate-12 ring-offset-2 ring-offset-n-surface-1'
                    : ''
                "
                :style="{ backgroundColor: swatch.value }"
                @click="accentColor = swatch.value"
              />
            </div>
          </div>
        </OnboardingSection>
      </OnboardingPage>

      <template #stage>
        <ChatCornerScene
          :notifications="stageNotifications"
          :badge-count="selectedKeys.length"
          :open="page !== PAGE_CHANNELS"
          :handler="handler || 'ai'"
          :theme="theme"
          :accent-color="accentColor"
        />
      </template>
    </OnboardingSplitLayout>
    <InboxChannelsDialog
      ref="channelsDialogRef"
      :inboxes="inboxes"
      @connected="refetchInboxes"
      @closed="revertUnconnectedDialogChannels"
    />
  </div>
</template>
